import { BadgeCheck, ShieldCheck, AlertTriangle, Clock } from "lucide-react";
import { cn } from "@/lib/utils";
import type { LegalReviewStatus, VerificationStatus, TrustLevel } from "@/lib/agora/types";

/** Statut de vérification de l'AUTEUR (identité et qualité professionnelle). */
export function AuthorVerificationBadge({
  status,
  trustLevel,
  className,
}: {
  status: VerificationStatus;
  trustLevel?: TrustLevel;
  className?: string;
}) {
  if (status !== "verified") return null;
  const label = trustLevel === "trusted" ? "Auteur de confiance" : "Auteur vérifié";
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full border border-primary/30 bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary",
        className,
      )}
    >
      <BadgeCheck className="h-3 w-3" aria-hidden />
      {label}
    </span>
  );
}

/** Statut de vérification juridique du CONTENU — distinct de l'auteur. */
export function LegalReviewBadge({
  status,
  lastReviewedAt,
  className,
}: {
  status: LegalReviewStatus;
  lastReviewedAt?: string | null;
  className?: string;
}) {
  const reviewed = lastReviewedAt
    ? new Date(lastReviewedAt).toLocaleDateString("fr-FR", {
        timeZone: "Europe/Paris",
        day: "2-digit",
        month: "long",
        year: "numeric",
      })
    : null;

  if (status === "outdated") {
    return (
      <span
        className={cn(
          "inline-flex items-center gap-1 rounded-full border border-destructive/40 bg-destructive/10 px-2 py-0.5 text-[11px] font-medium text-destructive",
          className,
        )}
      >
        <AlertTriangle className="h-3 w-3" aria-hidden />
        Analyse potentiellement obsolète
      </span>
    );
  }

  if (status === "reviewed") {
    return (
      <span
        className={cn(
          "inline-flex items-center gap-1 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2 py-0.5 text-[11px] font-medium text-emerald-600 dark:text-emerald-400",
          className,
        )}
      >
        <ShieldCheck className="h-3 w-3" aria-hidden />
        {reviewed ? `Contenu vérifié le ${reviewed}` : "Contenu vérifié"}
      </span>
    );
  }

  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full border border-border bg-muted/60 px-2 py-0.5 text-[11px] font-medium text-muted-foreground",
        className,
      )}
    >
      <Clock className="h-3 w-3" aria-hidden />
      Publié sous la responsabilité de l'auteur
    </span>
  );
}
